import { useEffect, useState } from "react"
import Button from "react-bootstrap/Button"
import Card from "react-bootstrap/Card"
import Col from "react-bootstrap/Col"
import Form from "react-bootstrap/Form"
import Modal from "react-bootstrap/Modal"
import Row from "react-bootstrap/Row"
import Spinner from "react-bootstrap/Spinner"
import Table from "react-bootstrap/Table"
import api from "./api.js"

function ExampleItems() {
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [error, setError] = useState("")

  const [createName, setCreateName] = useState("")
  const [createIsActive, setCreateIsActive] = useState(true)
  const [creating, setCreating] = useState(false)

  const [editOpen, setEditOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [editName, setEditName] = useState("")
  const [editIsActive, setEditIsActive] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function reload() {
    setError("")
    setLoading(true)
    try {
      const res = await api.getExampleItems()
      setItems(res.data || [])
    } catch (e) {
      setError(e.message || "Failed to load Example Items")
    } finally {
      setLoading(false)
    }
  }

  async function onCreateSubmit(e) {
    e.preventDefault()
    if (!createName.trim()) return

    setCreating(true)
    setError("")
    try {
      await api.createExampleItem({ name: createName.trim(), is_active: createIsActive })
      setCreateName("")
      setCreateIsActive(true)
      await reload()
    } catch (e2) {
      setError(e2.message || "Failed to create")
    } finally {
      setCreating(false)
    }
  }

  function openEdit(item) {
    setEditId(item.id)
    setEditName(item.name || "")
    setEditIsActive(item.is_active ?? true)
    setEditOpen(true)
  }

  async function onSaveEdit() {
    if (!editId) return
    setSaving(true)
    setError("")
    try {
      await api.updateExampleItem(editId, { name: editName.trim(), is_active: editIsActive })
      setEditOpen(false)
      await reload()
    } catch (e) {
      setError(e.message || "Failed to update")
    } finally {
      setSaving(false)
    }
  }

  async function onDelete(item) {
    if (!item?.id) return
    if (!confirm(`Delete "${item.name ?? "item"}" (${item.id})?`)) return

    setError("")
    try {
      await api.deleteExampleItem(item.id)
      await reload()
    } catch (e) {
      setError(e.message || "Failed to delete")
    }
  }

  return (
    <div className="container mt-4">
      <Row className="mb-3">
        <Col>
          <h2 className="fs-3 fw-semibold mb-0">Example Items</h2>
          <div className="text-secondary">Bare-bones CRUD UI for migration testing.</div>
        </Col>
        <Col className="col-auto d-flex align-items-center">
          <Button variant="outline-secondary" onClick={reload} disabled={loading}>Refresh</Button>
        </Col>
      </Row>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={onCreateSubmit}>
            <Row className="g-2 align-items-end">
              <Col>
                <Form.Label className="mb-1">Name</Form.Label>
                <Form.Control value={createName} onChange={e => setCreateName(e.target.value)} placeholder="New item name" />
              </Col>
              <Col className="col-auto">
                <Form.Check type="switch" label="Active" checked={createIsActive} onChange={e => setCreateIsActive(e.target.checked)} />
              </Col>
              <Col className="col-auto">
                <Button type="submit" disabled={creating || !createName.trim()}>{creating ? "Creating..." : "Create"}</Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-4 d-flex justify-content-center">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: "140px" }}>ID</th>
                  <th>Name</th>
                  <th style={{ width: "110px" }}>Active</th>
                  <th style={{ width: "260px" }}></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={4} className="text-secondary p-4">No items yet.</td></tr>
                ) : items.map(item => (
                  <tr key={item.id}>
                    <td className="text-secondary">{item.id}</td>
                    <td className="fw-semibold">{item.name}</td>
                    <td>{item.is_active ? "Yes" : "No"}</td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(item)}>Edit</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => onDelete(item)}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={editOpen} onHide={() => setEditOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Example Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control value={editName} onChange={e => setEditName(e.target.value)} />
          </Form.Group>
          <Form.Check type="switch" label="Active" checked={editIsActive} onChange={e => setEditIsActive(e.target.checked)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={onSaveEdit} disabled={saving || !editName.trim()}>{saving ? "Saving..." : "Save"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default ExampleItems

